import React from 'react';
import pt from 'prop-types';
import semver from 'semver';
import { connect } from 'react-redux';
import actions from 'partners/actions';
import LoadingView from 'components/view/loading';
import NotFoundPage from 'components/pages/error/404';
import { AppView } from 'partners/components/app';
import AppReviewDeleteMessage from 'components/apps/review/delete-message';
import { getMissingReviewFields } from 'utils/app';

export const mapStateToProps = (state) => ({
  session: state.session,
  lookup: state.lookup,
});

export const mapDispatchToProps = (dispatch) => ({
  fetchApp: (id) => {
    return dispatch(actions.apps.fetch(id));
  },

  fetchVersion: (id, version) => {
    return dispatch(actions.apps.fetchVersion(id, version));
  },

  publishVersion: (id, version) => {
    return dispatch(actions.apps.publish(id, version));
  },

  unpublishVersion: (id, version) => {
    return dispatch(actions.apps.unpublish(id, version));
  },

  submitReview: (id, version) => {
    return dispatch(actions.apps.submitReview(id, version));
  },

  createComment: (id, data) => {
    return dispatch(actions.apps.comments.create(id, data));
  },

  updateComment: (id, commentId, data) => {
    return dispatch(actions.apps.comments.update(id, commentId, data));
  },

  deleteComment: (id, commentId) => {
    return dispatch(actions.apps.comments.remove(id, commentId));
  },
});

export class ViewApp extends React.PureComponent {
  static contextTypes = {
    openModal: pt.func.isRequired,
  };

  static propTypes = {
    params: pt.object,
    location: pt.object,
    router: pt.object,
    fetchApp: pt.func,
    fetchVersion: pt.func,
    publishVersion: pt.func,
    unpublishVersion: pt.func,
    submitReview: pt.func,
    createComment: pt.func,
    updateComment: pt.func,
    deleteComment: pt.func,
  };

  constructor(props) {
    super(props);
    this.state = {
      loaded: false,
      app: null,
      record: null,
      versionRecord: null,
      staged: false,
      published: false,
      values: {},
      onSubmitReview: this.onSubmitReview.bind(this),
      onPublishRelease: this.onPublishRelease.bind(this),
      onUnpublishRelease: this.onUnpublishRelease.bind(this),
      onCreateComment: this.onCreateComment.bind(this),
      onUpdateComment: this.onUpdateComment.bind(this),
      onDeleteComment: this.onDeleteComment.bind(this),
    };
  }

  componentDidMount() {
    const { params, location } = this.props;
    const { version } = location.query;

    this.fetchRecord(params.id, version);
  }

  componentDidUpdate(prevProps) {
    const { params, location } = this.props;
    const { params: prevParams, location: prevLocation } = prevProps;
    const { version } = location.query;

    if (prevParams.id !== params.id || prevLocation.query.version !== version) {
      this.setState({ loaded: false });

      this.fetchRecord(params.id, version);
    }
  }

  // A failed request is reported by the store middleware and resolves empty
  async fetchRecord(id, version) {
    const { params, router, location, fetchApp, fetchVersion } = this.props;
    const { version: queryVersion } = location.query;

    const app = await fetchApp(id);

    if (!app) {
      this.setState({ loaded: true, record: null });
      return;
    }

    const nextVersion = version || queryVersion;
    const defaultVersion = this.getDefaultVersion(app);

    if (!nextVersion) {
      router.replace(`/apps/${params.id}?version=${defaultVersion}`);
      return;
    }

    const versionResult = await fetchVersion(app.id, nextVersion);

    if (!versionResult) {
      // Unknown version: fall back to the default one
      if (nextVersion !== defaultVersion) {
        router.replace(`/apps/${params.id}?version=${defaultVersion}`);
      } else {
        this.setState({ loaded: true, record: null });
      }
      return;
    }

    // The version comes with the app record as of that version; the current
    // version of an unreleased app comes without a version record
    const { app: record, ...versionFields } = versionResult;
    const versionRecord = versionFields.id ? versionFields : null;
    const { staged_props, published_props } = app;

    // Use staged or published props over the versioned app record
    const staged =
      staged_props?.version && staged_props.version === nextVersion;
    const published =
      published_props?.version && published_props.version === nextVersion;
    const usePublishedProps =
      published_props?.version &&
      semver.compare(nextVersion, published_props.version) <= 0;

    const appProps = staged
      ? staged_props
      : usePublishedProps
      ? published_props
      : record;
    const values = { ...record, ...appProps };

    this.setState({
      app,
      record,
      versionRecord,
      values: {
        ...values,
        // Apps aren't required to set their own support email; fall back to the partner's
        support_email: values.support_email || app.partner?.support_email,
      },
      staged,
      published,
      loaded: true,
    });
  }

  getDefaultVersion(app) {
    return (
      app.staged_props?.version ||
      app.published_props?.version ||
      app.publishable_versions?.results?.[0]?.version ||
      app.version
    );
  }

  async submitRelease(version) {
    const { submitReview } = this.props;
    const { app } = this.state;

    const result = await submitReview(app.id, version);

    if (result) {
      await this.fetchRecord(app.id, version);
    }
  }

  async publishRelease(version) {
    const { publishVersion } = this.props;
    const { app } = this.state;

    const result = await publishVersion(app.id, version);

    if (result) {
      await this.fetchRecord(app.id, version);
    }
  }

  async unpublishRelease(version) {
    const { unpublishVersion } = this.props;
    const { app } = this.state;

    const result = await unpublishVersion(app.id, version);

    if (result) {
      await this.fetchRecord(app.id);
    }
  }

  async onSubmitReview(version) {
    const { fetchVersion } = this.props;
    const { app } = this.state;

    // The release section offers the newest publishable version regardless
    // of which version is in view, so `state.app`/`state.record` cannot be
    // trusted to match `version`: read that version's own data instead.
    const versionResult = await fetchVersion(app.id, version);

    if (!versionResult) {
      return;
    }

    const reviewErrors = getMissingReviewFields(versionResult.app);

    if (reviewErrors.length > 0) {
      this.context.openModal('Confirm', {
        title: `Can't submit ${app.name} for review`,
        message: (
          <>
            <p>
              This app is missing required information and can&apos;t be
              submitted for review yet:
            </p>
            <ul>
              {reviewErrors.map(({ field, label, tip }) => (
                <li key={field}>
                  <strong>{label}</strong> &mdash; {tip}
                </li>
              ))}
            </ul>
          </>
        ),
        width: 750,
        actionHidden: true,
        cancelText: 'Close',
      });
      return;
    }

    this.context.openModal('Confirm', {
      title: `Submit ${app.name} ${version} for review`,
      message: `Submitting this release for review will snapshot the current app details. The app can be published at any time, when ready.`,
      width: 600,
      onConfirm: () => {
        return this.submitRelease(version);
      },
    });
  }

  onPublishRelease(versionRecord) {
    const { app } = this.state;
    const { review_status: reviewStatus, version } = versionRecord;

    const message = !reviewStatus
      ? `This will skip the review process and publish the app immediately. Are you sure?`
      : `This will publish the app. Are you sure?`;

    this.context.openModal('Confirm', {
      title: `Publish ${app.name} ${version}`,
      message,
      onConfirm: () => {
        return this.publishRelease(version);
      },
    });
  }

  onUnpublishRelease(version) {
    const { app } = this.state;

    this.context.openModal('Confirm', {
      title: `Unpublish ${app.name} ${version}`,
      message: `This will remove the app version from the App Store. Are you sure?`,
      onConfirm: () => {
        return this.unpublishRelease(version);
      },
    });
  }

  async onCreateComment(values) {
    const { location, createComment } = this.props;
    const { app } = this.state;
    const { version } = location.query;

    await createComment(app.id, values);
    await this.fetchRecord(app.id, version);
  }

  async onUpdateComment(id, values) {
    const { location, updateComment } = this.props;
    const { app } = this.state;
    const { version } = location.query;

    await updateComment(app.id, id, values);
    await this.fetchRecord(app.id, version);
  }

  onDeleteComment(comment) {
    const { location, deleteComment } = this.props;
    const { app } = this.state;
    const { version } = location.query;

    this.context.openModal('ConfirmDelete', {
      title: 'comment',
      action: 'Confirm',
      message: <AppReviewDeleteMessage comment={comment} />,
      width: 600,
      onConfirm: async () => {
        await deleteComment(app.id, comment.id);
        await this.fetchRecord(app.id, version);
      },
    });
  }

  render() {
    if (!this.state.loaded) {
      return <LoadingView />;
    }

    if (!this.state.record) {
      return <NotFoundPage />;
    }

    return <AppView {...this.props} {...this.state} />;
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(ViewApp);
